import type { PortalAssemblyTask } from '@proerp/shared';

/**
 * Anzeige-Aufbereitung der Montage-Einsätze. Reine Funktionen — die Trennung
 * „anstehend / erledigt" ist die eigentliche Aussage der Seite und soll ohne
 * DOM prüfbar sein.
 */

export interface AssemblySplit {
  open: PortalAssemblyTask[];
  done: PortalAssemblyTask[];
}

/**
 * Trennt die Einsätze in „steht an" und „erledigt".
 *
 * Maßgeblich ist der **Stand**, nicht das Datum: Ein Einsatz, der terminlich
 * vorbei ist, aber noch auf PLANNED steht, ist nicht erledigt — er ist offen
 * und gehört nach oben. Am Datum festzumachen würde genau die Fälle verstecken,
 * bei denen jemand nachhaken muss.
 */
export function splitAssemblyTasks(tasks: readonly PortalAssemblyTask[]): AssemblySplit {
  const open: PortalAssemblyTask[] = [];
  const done: PortalAssemblyTask[] = [];
  for (const task of tasks) {
    if (task.status === 'DONE' || task.status === 'CANCELLED') done.push(task);
    else open.push(task);
  }
  return { open, done };
}

/** Deutsche Bezeichnung des Stands. Unbekannte Werte bleiben unverändert. */
export function assemblyStatusLabel(status: string): string {
  switch (status) {
    case 'PLANNED':
      return 'Geplant';
    case 'CONFIRMED':
      return 'Bestätigt';
    case 'DONE':
      return 'Abgeschlossen';
    case 'CANCELLED':
      return 'Abgesagt';
    default:
      return status;
  }
}

/**
 * Zeitraum eines Einsatzes in Worten. Anders als bei der Bedarfsvorschau
 * **tagesgenau**: Ein Monteur muss wissen, wann er anreist — „September 2026"
 * hilft ihm nicht.
 */
export function formatAssemblyPeriod(start: string | null, end: string | null): string {
  const from = toDate(start);
  const to = toDate(end);
  if (!from && !to) return 'Termin offen';
  if (from && to) {
    // Eintägig: nicht „01.09.2026 – 01.09.2026" schreiben.
    if (day(from) === day(to)) return day(from);
    return `${day(from)} – ${day(to)}`;
  }
  return from ? `ab ${day(from)}` : `bis ${day(to as Date)}`;
}

function toDate(iso: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function day(date: Date): string {
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}
